import { useEffect, useState } from "react";
import axios from "axios";
import { Gift, Crown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PODIUM = [
  { rank: 1, h: "h-28", chip: "bg-[#D4AF37] text-black", glow: "shadow-[0_0_48px_-12px_#D4AF37]" },
  { rank: 2, h: "h-20", chip: "bg-neutral-300 text-black", glow: "" },
  { rank: 3, h: "h-16", chip: "bg-amber-700 text-black",   glow: "" },
];

export default function TopGifterCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/community/top-gifter?limit=5`);
        if (!cancelled) setData(data);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-[#0F0F0F] border border-zinc-800 p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (!data) return null;

  const rows = data.rows || [];
  const pinned = data.pinned_last_week;

  // Render-empty fallback so the card doesn't disappear before any gifts happen
  if (rows.length === 0 && !pinned) {
    return (
      <div
        data-testid="top-gifter-card"
        className="bg-[#0F0F0F] border border-zinc-800 p-6 text-center"
      >
        <Gift size={28} className="text-[#D4AF37] mx-auto mb-2" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Top Gifter This Week
        </div>
        <h3 className="font-display text-2xl text-white mt-1">
          Be the first 🎁
        </h3>
        <p className="text-sm text-neutral-400 mt-2 max-w-md mx-auto">
          Send Phoenix Tokens to friends and climb the leaderboard. The #1
          gifter each week earns the <span className="text-[#D4AF37] font-bold">Generous Athlete</span> badge.
        </p>
      </div>
    );
  }

  // Build podium (top 3) + list (4-5)
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);
  // Order podium visually: 2nd · 1st · 3rd
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div
      data-testid="top-gifter-card"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 relative overflow-hidden"
    >
      {/* Glow accent */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#D4AF37] rounded-full blur-[100px]" />
      </div>

      <div className="relative">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Gift size={12} />
              Top Gifter This Week
            </div>
            <h3 className="font-display text-2xl text-white leading-tight">
              Most generous athletes
            </h3>
          </div>
          {pinned && (
            <div
              data-testid="top-gifter-pinned"
              className="bg-[#1a0d2b] border-2 border-[#D4AF37] px-3 py-2 flex items-center gap-2 max-w-xs"
            >
              <Crown size={16} className="text-[#D4AF37] flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-[#D4AF37] font-bold leading-none">
                  Last Week's Champion
                </div>
                <div className="text-sm text-white font-display truncate mt-0.5">
                  {pinned.name} · {pinned.total_pt} Phoenix Tokens
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Podium */}
        {podiumOrder.length > 0 && (
          <div className="flex items-end justify-center gap-3 my-6">
            {podiumOrder.map((u) => {
              const meta = PODIUM.find((p) => p.rank === (top3[0] === u ? 1 : top3[1] === u ? 2 : 3));
              if (!meta) return null;
              return (
                <div
                  key={u.user_id}
                  data-testid={`top-gifter-podium-${meta.rank}`}
                  className="flex-1 max-w-[140px] flex flex-col items-center"
                >
                  {u.picture ? (
                    <img
                      src={u.picture}
                      alt={u.name}
                      className={`w-14 h-14 object-contain bg-black border-2 ${
                        meta.rank === 1 ? "border-[#D4AF37]" : "border-zinc-700"
                      } ${meta.glow}`}
                    />
                  ) : (
                    <div
                      className={`w-14 h-14 bg-[#4C1D95] border-2 ${
                        meta.rank === 1 ? "border-[#D4AF37]" : "border-zinc-700"
                      } flex items-center justify-center font-display text-2xl text-[#D4AF37] ${meta.glow}`}
                    >
                      {(u.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="font-display text-sm text-white text-center mt-2 leading-tight truncate w-full px-1">
                    {u.name}
                  </div>
                  <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">
                    {u.total_pt} Phoenix Tokens · {u.gifts}🎁
                  </div>
                  <div
                    className={`w-full mt-2 ${meta.h} ${meta.chip} flex items-center justify-center font-display text-2xl border-t-2 border-l-2 border-r-2 ${
                      meta.rank === 1 ? "border-[#D4AF37]" : "border-zinc-700"
                    }`}
                  >
                    {meta.rank}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rest of leaderboard (rows 4-5) */}
        {rest.length > 0 && (
          <div className="space-y-2 mt-4">
            {rest.map((u, i) => (
              <div
                key={u.user_id}
                data-testid={`top-gifter-row-${i + 4}`}
                className="flex items-center gap-3 bg-[#0A0A0A] border border-zinc-900 p-2"
              >
                <span className="w-8 h-8 bg-zinc-900 text-neutral-400 flex items-center justify-center font-display text-sm flex-shrink-0">
                  {i + 4}
                </span>
                {u.picture ? (
                  <img src={u.picture} alt={u.name} className="w-8 h-8 object-contain bg-black border border-zinc-700" />
                ) : (
                  <div className="w-8 h-8 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center text-xs font-display text-[#D4AF37] flex-shrink-0">
                    {(u.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{u.name}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-[#D4AF37]">{u.total_pt} Phoenix Tokens</div>
                  <div className="text-[9px] text-neutral-500 uppercase tracking-widest">{u.gifts}🎁</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-6 text-center">
          Week starts every Monday 00:00 PT · Winner pinned through next week
        </p>
      </div>
    </div>
  );
}
